import React, { useState } from 'react';
import {
  demoChapterStackColumnStyle,
  demoSectionCardStackStyle,
  DEMO_MINI_CARD_GRID_GAP_PX,
  FERTILITY_DEMO_SECTION_BORDER,
  linkInline,
} from '../designSystem';

const FONT = '"Plus Jakarta Sans", -apple-system, sans-serif';

const cardBase = {
  background: 'var(--white)',
  border: FERTILITY_DEMO_SECTION_BORDER,
  borderRadius: 14,
  padding: '16px 18px',
};

const protocolWhiteCard = {
  background: 'var(--white)',
  border: FERTILITY_DEMO_SECTION_BORDER,
  borderRadius: 14,
  padding: '16px 18px',
};

/** MedCoach pages + Wistia IDs from each page’s embed — poster = real video still (not a gradient). */
const INJECTION_VIDEO_GUIDES = {
  gonal: {
    href: 'https://mdrmedcoach.com/videos-guides/gonal-f-rff/',
    wistiaId: 's8bmrf899k',
  },
  menopur: {
    href: 'https://mdrmedcoach.com/videos-guides/menopur-q-cap-subcutaneous/',
    wistiaId: '0m9zkic38o',
  },
  ganirelix: {
    href: 'https://mdrmedcoach.com/videos-guides/ganirelix-acetate/',
    wistiaId: '69awplndn1',
  },
};

/** Wistia-hosted JPEG thumbnail for the video (same frame their player uses for the poster). */
function wistiaPosterUrl(wistiaId) {
  return `https://fast.wistia.net/embed/medias/${wistiaId}/swatch`;
}

/** 10 protocol days → dot state for Option C (single row of 9×9 dots). */
function dotStyleForDay(day, variant) {
  let state;
  if (variant === 'stim') {
    if (day < 8) state = 'done';
    else if (day === 8) state = 'today';
    else state = 'future';
  } else {
    if (day <= 5) state = 'na';
    else if (day < 8) state = 'done';
    else if (day === 8) state = 'today';
    else state = 'future';
  }
  const base = { width: 9, height: 9, borderRadius: 2, flexShrink: 0 };
  if (state === 'done') return { ...base, background: 'rgb(225, 245, 238)' };
  if (state === 'today') return { ...base, background: 'rgb(153, 53, 86)' };
  if (state === 'na') return { ...base, background: 'transparent', border: '1px dashed rgb(210, 208, 204)' };
  return { ...base, background: 'rgb(232, 224, 216)' };
}

function MedDotDays({ variant }) {
  const days = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  return (
    <div className="med-dot-days" style={{ display: 'flex', flexWrap: 'wrap', gap: 3, rowGap: 3, justifyContent: 'flex-end' }}>
      {days.map((d) => (
        <div key={d} className="med-dot" style={dotStyleForDay(d, variant)} title={`Day ${d}`} />
      ))}
    </div>
  );
}

/** Clear “log this dose” affordance — completed vs still to log. */
function MedLogCheck({ done }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 5,
        width: 76,
        flexShrink: 0,
        paddingTop: 2,
      }}
    >
      {done ? (
        <>
          <button
            type="button"
            style={{
              width: 26,
              height: 26,
              borderRadius: '50%',
              background: 'rgb(29, 158, 117)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white',
              fontSize: 12,
              fontWeight: 700,
              padding: 0,
              flexShrink: 0,
            }}
            aria-label="Dose logged"
          >
            ✓
          </button>
          <span
            style={{
              fontSize: 9,
              fontWeight: 700,
              color: 'rgb(8, 80, 65)',
              textAlign: 'center',
              lineHeight: 1.2,
            }}
          >
            Dose logged
          </span>
        </>
      ) : (
        <>
          <button
            type="button"
            style={{
              width: 26,
              height: 26,
              borderRadius: '50%',
              background: 'rgb(255, 252, 249)',
              border: '2px solid rgba(212, 83, 126, 0.45)',
              padding: 0,
              cursor: 'pointer',
              flexShrink: 0,
              boxShadow: '0 0 0 1px rgba(212, 83, 126, 0.12)',
            }}
            aria-label="Tap when you have taken this medicine"
          />
          <span
            style={{
              fontSize: 9,
              fontWeight: 700,
              color: 'rgb(153, 53, 86)',
              textAlign: 'center',
              lineHeight: 1.2,
            }}
          >
            Not logged yet
          </span>
          <span
            style={{
              fontSize: 8,
              fontWeight: 500,
              color: 'rgb(122, 107, 99)',
              textAlign: 'center',
              lineHeight: 1.25,
            }}
          >
            Tap when you have taken it
          </span>
        </>
      )}
    </div>
  );
}

function MedVideoThumb({ href, posterUrl, medName }) {
  const oneLine = `Injection guide · ${medName}`;
  const label = `${oneLine} (MedCoach — opens in new tab)`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      title={label}
      style={{
        width: 62,
        boxSizing: 'border-box',
        borderRadius: 8,
        padding: 4,
        background: 'rgb(255, 252, 249)',
        border: '0.5px solid rgb(232, 224, 216)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 4,
        flexShrink: 0,
        textDecoration: 'none',
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          width: '100%',
          height: 30,
          borderRadius: 5,
          backgroundColor: 'rgb(26, 21, 24)',
          backgroundImage: posterUrl ? `url(${posterUrl})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          border: '0.5px solid rgba(212, 83, 126, 0.25)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: 'rgb(212, 83, 126)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            zIndex: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
          }}
          aria-hidden
        >
          <span
            style={{
              width: 0,
              height: 0,
              borderStyle: 'solid',
              borderWidth: '3px 0 3px 5px',
              borderColor: 'transparent transparent transparent #fff',
              marginLeft: 1,
            }}
          />
        </span>
      </div>
      <span
        style={{
          fontSize: 8,
          fontWeight: 600,
          color: 'rgb(122, 107, 99)',
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          textAlign: 'center',
        }}
      >
        {oneLine}
      </span>
    </a>
  );
}

function TonightMedRow({
  done,
  name,
  dose,
  badgeTaken,
  variant,
  guideHref,
  guidePosterUrl,
  videoMedName,
  borderPink,
  last,
  dayCountText,
}) {
  const badgeStyle = badgeTaken
    ? { background: 'rgb(225, 245, 238)', color: 'rgb(8, 80, 65)' }
    : { background: 'rgb(251, 234, 240)', color: 'rgb(153, 53, 86)' };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        border: borderPink ? '0.5px solid rgba(212, 83, 126, 0.35)' : '0.5px solid rgb(232, 224, 216)',
        borderRadius: 10,
        padding: '10px 12px',
        marginBottom: last ? 0 : 6,
        background: 'rgb(255, 252, 249)',
        minHeight: 0,
      }}
    >
      <MedVideoThumb href={guideHref} posterUrl={guidePosterUrl} medName={videoMedName} />
      <MedLogCheck done={done} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'rgb(17, 17, 17)',
            lineHeight: 1.3,
          }}
        >
          {name}
        </div>
        <div
          style={{
            marginTop: 3,
            fontSize: 11,
            fontWeight: 400,
            color: 'rgb(122, 107, 99)',
            lineHeight: 1.45,
          }}
        >
          {dose}
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 4,
          flexShrink: 0,
          maxWidth: 132,
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            fontSize: 10,
            fontWeight: 600,
            padding: '2px 9px',
            borderRadius: 10,
            ...badgeStyle,
          }}
        >
          {badgeTaken ? 'Taken at 8:07pm' : '8am tomorrow'}
        </div>
        <MedDotDays variant={variant} />
        <div className="med-day-count" style={{ fontSize: 10, fontWeight: 400, color: 'rgb(122, 107, 99)' }}>
          {dayCountText}
        </div>
      </div>
    </div>
  );
}

function ReminderToggle({ on }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 11, color: 'var(--muted)' }}>Reminder on</span>
      <div
        style={{
          width: 28,
          height: 16,
          borderRadius: 8,
          background: on ? 'var(--sage)' : 'var(--sand-2)',
          position: 'relative',
        }}
      >
        <div
          style={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: 'var(--white)',
            position: 'absolute',
            top: 2,
            left: on ? 'auto' : 2,
            right: on ? 2 : 'auto',
          }}
        />
      </div>
    </div>
  );
}

const defaultModalMeds = [
  { id: '1', name: 'Gonal-F', dose: '225', unit: 'IU', schedule: 'Nightly 8pm', days: 'Days 1–10' },
  { id: '2', name: 'Menopur', dose: '75', unit: 'IU', schedule: 'Nightly 8pm', days: 'Days 1–10' },
  { id: '3', name: 'Ganirelix', dose: '250', unit: 'mcg', schedule: 'Morning 8am', days: 'Days 6–10' },
];

export default function FertilityDemoMedications({ navigate, omitHeader }) {
  const [pastOpen, setPastOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [protocolName, setProtocolName] = useState('Antagonist · Cycle 2');
  const [cycleNum, setCycleNum] = useState('2');
  const [startDate, setStartDate] = useState('Mar 27, 2026');
  const [clinic, setClinic] = useState('Pinnacle Fertility');
  const [modalMeds, setModalMeds] = useState(() => defaultModalMeds.map((m) => ({ ...m })));

  const updateMed = (id, field, value) => {
    setModalMeds((rows) => rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const removeMed = (id) => {
    setModalMeds((rows) => rows.filter((r) => r.id !== id));
  };

  const addMed = () => {
    setModalMeds((rows) => [
      ...rows,
      {
        id: `n-${Date.now()}`,
        name: '',
        dose: '',
        unit: 'IU',
        schedule: '',
        days: '',
      },
    ]);
  };

  return (
    <div style={demoChapterStackColumnStyle}>
      {!omitHeader && (
        <div
          style={{
            background: 'linear-gradient(140deg, rgb(251,234,240), rgb(225,245,238))',
            border: FERTILITY_DEMO_SECTION_BORDER,
            padding: '22px 26px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 12,
            flexWrap: 'wrap',
            borderRadius: 'var(--r, 14px)',
          }}
        >
          <div>
            <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#993556', marginBottom: 3 }}>
              Medications · IVF Cycle 2
            </div>
            <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: '0 0 4px 0' }}>
              Your protocol
            </h1>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>Log your protocol, set reminders, and look back at past cycles</div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/signup')}
            style={{
              fontFamily: FONT,
              fontSize: 12,
              fontWeight: 700,
              background: 'var(--pink)',
              color: 'white',
              border: 'none',
              borderRadius: 999,
              padding: '7px 16px',
              cursor: 'pointer',
            }}
          >
            Log Your Protocol & Get Reminders
          </button>
        </div>
      )}

      <div style={demoSectionCardStackStyle}>
      {/* SECTION 1 — Tonight's protocol */}
      <div style={protocolWhiteCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
          <div>
            <h2 className="section-title">Tonight&apos;s protocol</h2>
            <p className="section-descriptor">Stim day 8 · Antagonist · all 3 injections at 9pm</p>
          </div>
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            style={{ fontFamily: FONT, ...linkInline, textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}
          >
            Edit protocol →
          </button>
        </div>
        <TonightMedRow
          done
          name="Gonal-F 225 IU"
          dose="Nightly 8pm · Days 1–10 · subcutaneous"
          badgeTaken
          variant="stim"
          guideHref={INJECTION_VIDEO_GUIDES.gonal.href}
          guidePosterUrl={wistiaPosterUrl(INJECTION_VIDEO_GUIDES.gonal.wistiaId)}
          videoMedName="Gonal-F"
          dayCountText="Day 8 of 10"
        />
        <TonightMedRow
          done
          name="Menopur 75 IU"
          dose="Nightly 8pm · Days 1–10 · subcutaneous"
          badgeTaken
          variant="stim"
          guideHref={INJECTION_VIDEO_GUIDES.menopur.href}
          guidePosterUrl={wistiaPosterUrl(INJECTION_VIDEO_GUIDES.menopur.wistiaId)}
          videoMedName="Menopur"
          dayCountText="Day 8 of 10"
        />
        <TonightMedRow
          done={false}
          name="Ganirelix 250 mcg"
          dose="Morning 8am · Days 6–10 · subcutaneous"
          badgeTaken={false}
          variant="ganirelix"
          guideHref={INJECTION_VIDEO_GUIDES.ganirelix.href}
          guidePosterUrl={wistiaPosterUrl(INJECTION_VIDEO_GUIDES.ganirelix.wistiaId)}
          videoMedName="Ganirelix"
          borderPink
          last
          dayCountText="Day 3 of 5"
        />

        <div
          style={{
            marginTop: 10,
            paddingTop: 10,
            borderTop: '0.5px solid var(--sand-2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: 11,
            color: 'var(--muted)',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <span>All reminders set · next at 8am tomorrow</span>
          <button
            type="button"
            onClick={() => navigate('/signup')}
            style={{ fontFamily: FONT, background: 'none', border: 'none', padding: 0, color: 'var(--pink)', fontWeight: 600, cursor: 'pointer', fontSize: 11 }}
          >
            Manage reminders
          </button>
        </div>
      </div>

      {/* SECTION 2 — Full protocol */}
      <div style={protocolWhiteCard}>
        <div style={{ marginBottom: 12 }}>
          <h2 className="section-title">Your protocol</h2>
          <p className="section-descriptor">Cycle 2 · Antagonist · Days 1–10</p>
        </div>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>
          ALL MEDICATIONS
        </div>
        {[
          { name: 'Gonal-F 225 IU', dose: 'Nightly 8pm · Days 1–10' },
          { name: 'Menopur 75 IU', dose: 'Nightly 8pm · Days 1–10' },
          { name: 'Ganirelix 250 mcg', dose: 'Morning 8am · Days 6–10' },
        ].map((row, i, arr) => (
          <div
            key={row.name}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '9px 0',
              borderBottom: i < arr.length - 1 ? '0.5px solid var(--sand-2)' : 'none',
              gap: 12,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{row.name}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{row.dose}</div>
            </div>
            <ReminderToggle on />
          </div>
        ))}
        <button
          type="button"
          onClick={() => setEditOpen(true)}
          style={{ fontFamily: FONT, marginTop: 10, ...linkInline, textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}
        >
          Edit protocol →
        </button>
      </div>

      {/* SECTION 3 — Past protocols */}
      <div>
        <div style={{ ...cardBase, marginTop: 4 }}>
          <button
            type="button"
            onClick={() => setPastOpen((o) => !o)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              background: 'none',
              border: 'none',
              padding: 0,
              paddingBottom: pastOpen ? 10 : 0,
              borderBottom: pastOpen ? '0.5px solid var(--sand-2)' : 'none',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <div>
              <h2 className="section-title">Past protocols</h2>
              <p className="section-descriptor">2 previous cycles · tap to expand</p>
            </div>
            <span style={{ fontSize: 12, color: 'var(--muted)', flexShrink: 0 }}>{pastOpen ? '▲' : '▼'}</span>
          </button>

          {pastOpen ? (
            <div style={{ paddingTop: 12 }}>
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  padding: '10px 0',
                  borderBottom: '0.5px solid var(--sand-2)',
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: 'var(--sand-2)',
                    color: 'var(--muted)',
                    fontSize: 10,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  1
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Cycle 1 · Feb 2025 · Antagonist</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.45, marginTop: 1 }}>
                    Gonal-F 150 IU · Menopur 75 IU · Cetrotide 0.25mg
                    <br />
                    8 eggs retrieved · 6 fertilized · 4 blasts
                  </div>
                </div>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: 10,
                    background: 'var(--sage-l)',
                    color: '#085041',
                    flexShrink: 0,
                  }}
                >
                  Complete
                </span>
              </div>

              <div style={{ display: 'flex', gap: 10, padding: '10px 0', alignItems: 'flex-start' }}>
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: 'var(--sand-2)',
                    color: 'var(--muted)',
                    fontSize: 10,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  ❄
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Egg freeze · Oct 2024 · Mini IVF</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.45, marginTop: 1 }}>
                    Clomid 50mg · Gonal-F 75 IU · Cetrotide 0.25mg
                    <br />
                    5 eggs retrieved · 4 frozen
                  </div>
                </div>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: 10,
                    background: 'var(--sage-l)',
                    color: '#085041',
                    flexShrink: 0,
                  }}
                >
                  Complete
                </span>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      </div>

      {/* Edit protocol modal */}
      {editOpen ? (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
          onClick={() => setEditOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-protocol-title"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--white, #fffcf9)',
              borderRadius: 16,
              padding: '20px 24px',
              width: 520,
              maxWidth: '100%',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2 id="edit-protocol-title" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 18, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                Edit protocol
              </h2>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setEditOpen(false)}
                style={{ background: 'none', border: 'none', fontSize: 16, color: 'var(--muted)', cursor: 'pointer', padding: 4 }}
              >
                ✕
              </button>
            </div>

            <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>
              Protocol name
            </label>
            <input
              value={protocolName}
              onChange={(e) => setProtocolName(e.target.value)}
              style={{
                border: '0.5px solid var(--border)',
                borderRadius: 8,
                padding: '9px 12px',
                fontSize: 13,
                width: '100%',
                marginBottom: 14,
                fontFamily: FONT,
                boxSizing: 'border-box',
              }}
            />

            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>
              MEDICATIONS IN THIS PROTOCOL
            </div>

            {modalMeds.map((m, idx) => (
              <div
                key={m.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '9px 0',
                  borderBottom: idx < modalMeds.length - 1 ? '0.5px solid var(--sand-2)' : 'none',
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ fontSize: 12, color: 'var(--muted)', width: 14 }} aria-hidden>
                  ⠿
                </span>
                <input
                  value={m.name}
                  onChange={(e) => updateMed(m.id, 'name', e.target.value)}
                  style={{ width: 140, fontSize: 12, border: '0.5px solid var(--border)', borderRadius: 6, padding: '5px 8px', fontFamily: FONT, boxSizing: 'border-box' }}
                />
                <input
                  value={m.dose}
                  onChange={(e) => updateMed(m.id, 'dose', e.target.value)}
                  style={{ width: 60, fontSize: 12, border: '0.5px solid var(--border)', borderRadius: 6, padding: '5px 8px', fontFamily: FONT, boxSizing: 'border-box' }}
                />
                <select
                  value={m.unit}
                  onChange={(e) => updateMed(m.id, 'unit', e.target.value)}
                  style={{ width: 60, fontSize: 12, border: '0.5px solid var(--border)', borderRadius: 6, padding: '5px 4px', fontFamily: FONT, boxSizing: 'border-box' }}
                >
                  <option value="IU">IU</option>
                  <option value="mcg">mcg</option>
                  <option value="mg">mg</option>
                  <option value="mL">mL</option>
                </select>
                <input
                  value={m.schedule}
                  onChange={(e) => updateMed(m.id, 'schedule', e.target.value)}
                  style={{ width: 100, fontSize: 12, border: '0.5px solid var(--border)', borderRadius: 6, padding: '5px 8px', fontFamily: FONT, boxSizing: 'border-box' }}
                />
                <input
                  value={m.days}
                  onChange={(e) => updateMed(m.id, 'days', e.target.value)}
                  style={{ width: 80, fontSize: 12, border: '0.5px solid var(--border)', borderRadius: 6, padding: '5px 8px', fontFamily: FONT, boxSizing: 'border-box' }}
                />
                <button type="button" onClick={() => removeMed(m.id)} style={{ background: 'none', border: 'none', fontSize: 12, color: 'var(--muted)', cursor: 'pointer', padding: 4 }}>
                  ✕
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={addMed}
              style={{
                marginTop: 8,
                paddingTop: 8,
                borderTop: '0.5px solid var(--sand-2)',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: 'var(--pink)',
                fontWeight: 600,
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                width: '100%',
                fontFamily: FONT,
              }}
            >
              + Add medication
            </button>

            <div style={{ marginTop: 16, borderTop: '0.5px solid var(--sand-2)', paddingTop: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>
                CYCLE INFORMATION
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: DEMO_MINI_CARD_GRID_GAP_PX, marginBottom: 10 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Cycle number</label>
                  <input
                    value={cycleNum}
                    onChange={(e) => setCycleNum(e.target.value)}
                    style={{ width: '100%', border: '0.5px solid var(--border)', borderRadius: 8, padding: '9px 12px', fontSize: 13, fontFamily: FONT, boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Start date</label>
                  <input
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ width: '100%', border: '0.5px solid var(--border)', borderRadius: 8, padding: '9px 12px', fontSize: 13, fontFamily: FONT, boxSizing: 'border-box' }}
                  />
                </div>
              </div>
              <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Clinic</label>
              <input
                value={clinic}
                onChange={(e) => setClinic(e.target.value)}
                style={{ width: '100%', border: '0.5px solid var(--border)', borderRadius: 8, padding: '9px 12px', fontSize: 13, fontFamily: FONT, boxSizing: 'border-box' }}
              />
            </div>

            <button
              type="button"
              onClick={() => setEditOpen(false)}
              style={{
                width: '100%',
                marginTop: 16,
                background: 'var(--pink)',
                color: 'white',
                fontSize: 13,
                fontWeight: 600,
                padding: 12,
                borderRadius: 10,
                border: 'none',
                cursor: 'pointer',
                fontFamily: FONT,
              }}
            >
              Save protocol
            </button>
            <button
              type="button"
              onClick={() => setEditOpen(false)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'center',
                marginTop: 8,
                fontSize: 12,
                color: 'var(--muted)',
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                fontFamily: FONT,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
